/**
 * The business's own identity, for the document letterhead.
 *
 * A separate file from `documentReader.ts` for the same reason: reads open no
 * transaction, and the letterhead is a different concern from the document
 * being printed.
 *
 * THE INTERESTING PART IS THE BANK ACCOUNT
 * ---------------------------------------
 * A business can hold several, and the document must print exactly one. Picking
 * "the first" is how an invoice ends up paying into an account the owner closed
 * last year, so the rule is explicit and ordered:
 *
 *   1. the account marked `isDefault`, if there is one;
 *   2. otherwise none, because silently substituting an arbitrary account is
 *      worse than printing no bank details at all.
 *
 * An invoice with no bank block is incomplete but correct. An invoice with the
 * WRONG bank block sends money somewhere real.
 */
import type { Table } from 'dexie';
import type { Attachment, Business } from '../db/types';
import type { BusinessIdentity } from '@core/doc/renderA4';
import { readImageDataUrl } from './attachmentService';

/** The account printed in the letterhead, or undefined if there is no clear one. */
export function chooseBankAccount(business: Business): Business['bankAccounts'][number] | undefined {
  if (!Array.isArray(business.bankAccounts) || business.bankAccounts.length === 0) return undefined;
  return business.bankAccounts.find((a) => a.isDefault);
}

/**
 * Map a stored business onto the letterhead.
 *
 * Pure, so it is testable without a database, and because getting it wrong puts
 * a real company's name and bank details on a tax invoice.
 */
export function toBusinessIdentity(business: Business): BusinessIdentity {
  const bank = chooseBankAccount(business);

  // The address block is stored already split into lines; the city and pincode
  // are separate columns and belong on the last line. A letterhead that shows a
  // street but no city is not a letterhead.
  const addressLines = [...business.addressLines];
  const locality = [business.city, business.pincode].filter(Boolean).join(' - ');
  if (locality) addressLines.push(locality);

  return {
    legalName: business.legalName,
    // Carried onto the letterhead. Both were stored on the business and
    // dropped here, so a business that had configured either printed an
    // invoice missing information it already held.
    tradeName: business.tradeName,
    website: business.website,
    gstin: business.gstin,
    addressLines,
    phone: business.phone,
    email: business.email,
    stateName: business.stateName,
    bankName: bank?.bankName,
    accountNumber: bank?.accountNumber,
    ifsc: bank?.ifsc,
  };
}

export interface BusinessTables {
  businesses: Table<Business, string>;
  attachments: Table<Attachment, string>;
}

export class BusinessNotConfiguredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BusinessNotConfiguredError';
  }
}

/**
 * Read the business identity for printing.
 *
 * @throws BusinessNotConfiguredError when no business row exists, which on a
 *         fresh install is the normal first-run state and must be handled by
 *         the screen rather than rendered as a blank letterhead.
 */
export async function loadBusinessIdentity(
  db: BusinessTables,
  businessId: string,
): Promise<BusinessIdentity> {
  const business = await db.businesses.get(businessId);
  if (!business) {
    throw new BusinessNotConfiguredError(
      `No business with id "${businessId}". Printing a document with no letterhead ` +
        'produces an invoice nobody can attribute, so this must be loud.',
    );
  }
  return toBusinessIdentity(business);
}

/**
 * The same identity, with the logo and signature resolved to printable data URLs.
 *
 * `toBusinessIdentity` stays PURE and synchronous, so the projection of a
 * `Business` row into something printable is testable with no database and no
 * `FileReader`. Resolving an attachment cannot be either: reading bytes is
 * asynchronous, and that is exactly why it lives here instead.
 *
 * A missing or unreadable image resolves to `undefined` rather than throwing.
 * Printing must not fail because a logo row was lost in a restore - an invoice
 * without a logo is correct, an invoice that will not print is not.
 */
export async function loadBusinessIdentityWithImages(
  db: BusinessTables,
  businessId: string,
): Promise<BusinessIdentity> {
  const business = await db.businesses.get(businessId);
  if (!business) {
    throw new BusinessNotConfiguredError(
      `No business with id "${businessId}". Printing a document with no letterhead ` +
        'produces an invoice nobody can attribute, so this must be loud.',
    );
  }

  const [logoDataUrl, signatureDataUrl] = await Promise.all([
    readImageDataUrl(db, businessId, business.logoAttachmentId),
    readImageDataUrl(db, businessId, business.signatureAttachmentId),
  ]);

  // Spread the projection and add ONLY the two images. `toBusinessIdentity` is
  // the single place that decides what belongs on paper, and spreading the
  // business row here instead would quietly put every column - bank accounts,
  // FY settings - into the letterhead model.
  return { ...toBusinessIdentity(business), logoDataUrl, signatureDataUrl };
}
